import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

export interface WorkoutAction {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}

interface WorkoutActionBarProps {
  actions: WorkoutAction[];
  backLabel: string;
  onBack: () => void;
}

const WorkoutActionBar = ({
  actions,
  backLabel,
  onBack,
}: WorkoutActionBarProps) => (
  <div className="pointer-events-none fixed right-3 top-[calc(0.75rem+env(safe-area-inset-top))] z-[1100] md:sticky md:top-4 md:flex md:w-full md:items-center md:justify-between">
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={onBack}
      className="glass-control pointer-events-auto hidden h-11 w-11 rounded-full border-gray-200 bg-background/95 text-brand-midnight shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 md:inline-flex"
      aria-label={backLabel}
      title={backLabel}
    >
      <ArrowLeft className="h-5 w-5" />
    </Button>
    <div
      className="glass-action-bar pointer-events-auto flex max-w-[calc(100vw-5rem)] items-center gap-1 overflow-x-auto rounded-2xl border border-gray-200 bg-background/95 p-1.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      aria-label="Workout actions"
    >
      {actions.map((action, index) => (
        <span key={action.label} className={action.destructive ? 'ml-1 border-l border-gray-200 pl-1' : ''}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={action.onClick}
            disabled={action.disabled}
            className={`h-11 w-11 shrink-0 rounded-xl disabled:opacity-60 ${
              index === 0
                ? 'bg-brand-midnight text-brand-volt hover:bg-brand-slate'
                : action.destructive
                  ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                  : 'text-brand-midnight hover:bg-brand-blueSoft'
            }`}
            aria-label={action.label}
            title={action.label}
          >
            {action.icon}
          </Button>
        </span>
      ))}
    </div>
  </div>
);

export default WorkoutActionBar;
